import { storage } from "@wlink/commons/core";
import { defineStore } from "pinia";
import { ref } from "vue";
// 使用全局request函数
declare global {
  interface Window {
    request: (name: string, data?: any, id?: any) => Promise<any>;
  }
}

const getRequest = () => {
  // 如果window.request存在，使用它，否则返回一个mock函数
  return (
    window.request ||
    (async () => {
      throw new Error("Request function not available");
    })
  );
};

export interface LanguageItem {
  name: string;
  code: string;
}

// 新增国家货币相关类型定义
export interface CountryCurrencyItem {
  code: string;
  name: {
    en: string;
    "zh-CN": string;
    "zh-TW"?: string;
  };
  flag: string;
  phone_code?: string;
}

// 新增货币相关类型定义
export interface CurrencyItem {
  code: string;
  name: {
    en: string;
    zh: string;
  };
}

export interface CompanyConfig {
  /** 经纪商名称多语言 */
  broker_name_i18n?: {
    "zh-CN"?: string;
    "zh-TW"?: string;
    en?: string;
    [lang: string]: string | undefined;
  };
  /** 公司logo */
  company_logo_url?: string;
  /** 邮箱 */
  email?: string;
  /** 登录页logo */
  login_page_logo_url?: string;
  /** 服务电话（格式: +国际区号 号码） */
  support_phone?: string;
  /** 时区 */
  timezone?: string;
  /** 角标logo/水印logo */
  watermark_logo_url?: string;
  // 允许扩展
  [key: string]: any;
}

export interface GlobalConfig {
  languageList: LanguageItem[];
  companyConfig: CompanyConfig;
  countryCurrencyList: CountryCurrencyItem[]; // 新增国家货币列表
  currencyList: CurrencyItem[]; // 新增货币列表
}

export const useGlobalConfigStore = defineStore("globalConfig", () => {
  // 语言配置
  const languageList = ref<LanguageItem[]>([]);
  const isLanguageListLoaded = ref(false);

  // 公司配置
  const companyConfig = ref<CompanyConfig>({});
  const isCompanyConfigLoaded = ref(false);

  // 国家货币配置
  const countryCurrencyList = ref<CountryCurrencyItem[]>([]);
  const isCountryCurrencyListLoaded = ref(false);

  // 货币配置
  const currencyList = ref<CurrencyItem[]>([]);
  const isCurrencyListLoaded = ref(false);
  // 安全配置
  const securityConfig = ref<any>({});
  const isSecurityConfigLoaded = ref(false);
  // 投放列表
  const deliveryList = ref<any>([]);
  const isDeliveryListLoaded = ref(false);
  // 版本号
  const version = ref<string>("");
  const isVersionLoaded = ref(false);

  // 全局加载状态
  const isGlobalConfigLoaded = ref(false);

  // 获取语言列表
  const fetchLanguageList = async () => {
    try {
      const requestFn = getRequest();
      const response = await requestFn("GetLanguage");
      if (response.data?.language) {
        languageList.value = response.data.language;
        isLanguageListLoaded.value = true;
      }
    } catch (error) {
      // 如果接口失败，使用默认语言列表
      languageList.value = [
        { name: "简体中文", code: "zh-CN" },
        { name: "繁体中文", code: "zh-TW" },
        { name: "English", code: "en" },
      ];
      isLanguageListLoaded.value = true;
    }
  };

  // 获取国家货币列表
  const fetchCountryCurrencyList = async () => {
    try {
      const requestFn = getRequest();
      const response = await requestFn("GetCountryCurrencyList");
      if (response.code === 0 && response.data?.list) {
        countryCurrencyList.value = response.data.list;
        isCountryCurrencyListLoaded.value = true;
      }
    } catch (error) {
      // 如果接口失败，使用空列表
      countryCurrencyList.value = [];
      isCountryCurrencyListLoaded.value = true;
    }
  };

  // 获取货币列表
  const fetchCurrencyList = async () => {
    try {
      const requestFn = getRequest();
      const response = await requestFn("GetCurrencyList");
      if (response.code === 0 && response.data?.list) {
        currencyList.value = response.data.list;
        isCurrencyListLoaded.value = true;
      }
    } catch (error) {
      // 如果接口失败，使用空列表
      currencyList.value = [];
      isCurrencyListLoaded.value = true;
    }
  };
  // 获取公司设置配置
  const fetchCompanyConfig = async () => {
    try {
      const requestFn = getRequest();
      const response = await requestFn("GetCompany");
      if (response.code === 0 && response.data) {
        companyConfig.value = {
          broker_name_i18n: response.data.broker_name_i18n,
          company_logo_url: response.data.company_logo_url,
          email: response.data.email,
          login_page_logo_url: response.data.login_page_logo_url,
          support_phone: response.data.support_phone,
          timezone: response.data.timezone,
          watermark_logo_url: response.data.watermark_logo_url,
        };
        isCompanyConfigLoaded.value = true;

        // 自动设置favicon
        if (response.data.watermark_logo_url) {
          // 动态导入setFavicon函数避免循环依赖
          import("../../core/utils")
            .then(({ setFavicon }) => {
              setFavicon(response.data.watermark_logo_url);
            })
            .catch((error) => {});
        }
      }
    } catch (error) {
      companyConfig.value = {};
      isCompanyConfigLoaded.value = true;
    }
  };
  // 获取安全配置
  const fetchSecurityConfig = async () => {
    try {
      const requestFn = getRequest();
      const response = await requestFn("GetSecurity");
      if (response.code === 0 && response.data) {
        securityConfig.value = response.data;
        isSecurityConfigLoaded.value = true;
      }
    } catch (error) {
      // 如果接口失败，使用空列表
      securityConfig.value = {};
      isSecurityConfigLoaded.value = true;
    }
  };
  const isRequiredCaptcha = (scene: string) => {
    return (
      securityConfig.value?.email_verification_scenarios?.includes(scene) ??
      false
    );
  };
  // 获取投放信息
  const fetchDeliveryList = async () => {
    try {
      const requestFn = getRequest();
      const response = await requestFn("ListDelivery");
      if (response.code === 0 && response.data.list) {
        deliveryList.value = response.data.list || [];
        isDeliveryListLoaded.value = true;
      }
    } catch (error) {
      // 如果接口失败，使用空列表
      deliveryList.value = [];
      isDeliveryListLoaded.value = true;
    }
  };
  // 获取版本号
  const fetchVersion = async () => {
    try {
      const requestFn = getRequest();
      const response = await requestFn("GetVersion");
      if (response.code === 0 && response.data) {
        version.value = response.data.version;
        isVersionLoaded.value = true;
      }
    } catch (error) {
      version.value = "";
      isVersionLoaded.value = true;
    }
  };
  // 根据code获取语言名称
  const getLanguageName = (code: string) => {
    const lang = languageList.value.find(
      (item) => item.code === code || item.code === code.split("-")[0]
    );
    return lang?.name || code;
  };

  // 获取国家列表（用于下拉框）
  const getCountryList = (lang?: string) => {
    const target = normalizeLang(lang || "zh-CN");
    return countryCurrencyList.value.map((item) => {
      return {
        label: item.name[target],
        value: item.code,
        labelEn: item.name.en,
        labelZh: item.name["zh-CN"],
        labelZhTw: item.name["zh-TW"],
        flag: item.flag,
      };
    });
  };

  // 规范化语言码到 en / zh-CN / zh-TW
  const normalizeLang = (lang?: string): "en" | "zh-CN" | "zh-TW" => {
    if (!lang) return "zh-CN";
    const lower = lang.toLowerCase();
    if (lower.startsWith("en")) return "en";
    if (lower.startsWith("zh")) {
      if (lower.includes("tw") || lower.includes("hk")) return "zh-TW";
      return "zh-CN";
    }
    return "en";
  };

  // 根据国家代码获取国家名称（兼容 en / zh-CN / zh-TW，并向后兼容传入 zh/en）
  const getCountryName = (countryCode: string, lang: string = "zh-CN") => {
    const country = countryCurrencyList.value.find(
      (item) => item.code === countryCode
    );
    if (!country) return countryCode;
    const target = normalizeLang(lang);
    return (
      country.name[target] ||
      country.name["zh-CN"] ||
      country.name.en ||
      countryCode
    );
  };

  // 获取货币列表（用于下拉框）
  const getCurrencyList = () => {
    return currencyList.value.map((item) => ({
      label: item.name.zh || item.name.en,
      value: item.code,
      labelEn: item.name.en,
      labelZh: item.name.zh,
    }));
  };

  // 根据货币代码获取货币名称
  const getCurrencyName = (currencyCode: string, lang: "zh" | "en" = "zh") => {
    const currency = currencyList.value.find(
      (item) => item.code === currencyCode
    );
    return currency ? currency.name[lang] : currencyCode;
  };

  // 重新加载指定配置
  const reloadConfig = async (
    configType:
      | "language"
      | "company"
      | "countryCurrency"
      | "currency"
      | "security"
      | "all"
  ) => {
    try {
      switch (configType) {
        case "language":
          await fetchLanguageList();
          break;
        case "company":
          await fetchCompanyConfig();
          break;
        case "countryCurrency":
          await fetchCountryCurrencyList();
          break;
        case "currency":
          await fetchCurrencyList();
          break;
        case "security":
          await fetchSecurityConfig();
          break;
        case "all":
          await initGlobalConfig();
          break;
      }
    } catch (error) {}
  };

  // 初始化全局配置
  const initGlobalConfig = async (portal: string = "") => {
    const token = storage.get("token");
    try {
      // 设置超时，最多等待10秒
      const timeout = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("全局配置加载超时")), 10000);
      });

      const shouldFetchSecurityConfig =
        !isSecurityConfigLoaded.value && portal !== "admin-portal";
      const shouldFetchDeliveryList =
        !isDeliveryListLoaded.value && portal !== "admin-portal" && token;
      // 并行加载所有配置
      const configPromises = [
        !isLanguageListLoaded.value ? fetchLanguageList() : Promise.resolve(),
        !isCompanyConfigLoaded.value ? fetchCompanyConfig() : Promise.resolve(),
        !isCountryCurrencyListLoaded.value
          ? fetchCountryCurrencyList()
          : Promise.resolve(),
        !isCurrencyListLoaded.value ? fetchCurrencyList() : Promise.resolve(),
        shouldFetchSecurityConfig ? fetchSecurityConfig() : Promise.resolve(),
        shouldFetchDeliveryList ? fetchDeliveryList() : Promise.resolve(),
        !isVersionLoaded.value ? fetchVersion() : Promise.resolve(),
      ];

      await Promise.race([Promise.all(configPromises), timeout]);

      isGlobalConfigLoaded.value = true;
    } catch (error) {
      // 即使失败也标记为已加载，避免重复尝试
      isGlobalConfigLoaded.value = true;
    }
  };

  return {
    // 状态
    languageList,
    companyConfig,
    countryCurrencyList,
    currencyList,
    deliveryList,
    version,
    isLanguageListLoaded,
    isCompanyConfigLoaded,
    isCountryCurrencyListLoaded,
    isCurrencyListLoaded,
    isGlobalConfigLoaded,
    securityConfig,
    isSecurityConfigLoaded,
    isDeliveryListLoaded,
    // 方法
    isRequiredCaptcha,
    fetchLanguageList,
    fetchCompanyConfig,
    fetchCountryCurrencyList,
    fetchCurrencyList,
    fetchSecurityConfig,
    fetchDeliveryList,
    getLanguageName,
    getCountryList,
    getCountryName,
    getCurrencyList,
    getCurrencyName,
    reloadConfig,
    initGlobalConfig,
  };
});
